import { RefObject, useEffect, useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import FileDownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import alertService from "../../../components/alert/alertService";
import CustomModal from "../../../components/modal/CustomModal";
import { common, financedEmissions } from "../../../i18n";
import { DashboardData } from "../../../models/dto/cat15/cat15.types";

interface Props {
  open: boolean;
  onClose: () => void;
  data: DashboardData;
  /** The live dashboard content node — cloned and rasterized for the export. */
  targetRef: RefObject<HTMLElement>;
}

/** A rendered snapshot of the dashboard: the preview shown here IS the image
 *  that gets saved, so what you see is exactly what you get. */
interface Snapshot {
  dataUrl: string;
  width: number;
  height: number;
}

const ExportModal = ({ open, onClose, data, targetRef }: Props) => {
  const { t } = useTranslation([financedEmissions, common], {
    nsMode: "fallback",
  });
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [isRendering, setIsRendering] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const fileBase = `financed-emissions-dashboard-${data.reportingYear}`;

  // Whenever the modal opens, rasterize the live dashboard once. The clone is
  // laid out off-screen at a fixed width so the export is the full page at a
  // consistent size, not whatever the current viewport happens to be.
  useEffect(() => {
    if (!open) {
      setSnapshot(null);
      return;
    }
    const source = targetRef.current;
    if (!source) return;

    let cancelled = false;
    setIsRendering(true);

    const render = async () => {
      const cloned = source.cloneNode(true) as HTMLElement;
      Object.assign(cloned.style, {
        width: "1440px",
        padding: "32px",
        background: "var(--white)",
        boxSizing: "border-box",
        position: "absolute",
        left: "-9999px",
        top: "0",
      });
      document.body.appendChild(cloned);
      try {
        // Give the cloned charts a tick to lay out before rasterizing.
        await new Promise((resolve) => setTimeout(resolve, 80));
        const canvas = await html2canvas(cloned, {
          scale: 2,
          useCORS: true,
          backgroundColor: null,
        });
        if (!cancelled) {
          setSnapshot({
            dataUrl: canvas.toDataURL("image/png"),
            width: canvas.width,
            height: canvas.height,
          });
        }
      } catch {
        if (!cancelled) alertService.error(t("Export-Error"));
      } finally {
        if (document.body.contains(cloned)) {
          document.body.removeChild(cloned);
        }
        if (!cancelled) setIsRendering(false);
      }
    };

    render();
    return () => {
      cancelled = true;
    };
  }, [open, targetRef, t]);

  const handleExport = () => {
    if (!snapshot || isExporting) return;
    setIsExporting(true);
    try {
      const pdf = new jsPDF({
        orientation:
          snapshot.width >= snapshot.height ? "landscape" : "portrait",
        unit: "px",
        format: [snapshot.width, snapshot.height],
      });
      pdf.addImage(
        snapshot.dataUrl,
        "PNG",
        0,
        0,
        snapshot.width,
        snapshot.height,
      );
      pdf.save(`${fileBase}.pdf`);
      alertService.success(t("Export-Toast", { format: "PDF" }));
      onClose();
    } catch {
      alertService.error(t("Export-Error"));
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      maxWidth="760px"
      header={
        <Box>
          <Typography variant="h1">{t("Export-Dashboard")}</Typography>
          <Typography variant="body2" color="var(--gray1)" mt="4px">
            {t("Export-Subtitle")}
          </Typography>
        </Box>
      }
      body={
        <Box>
          <Typography variant="body2" color="var(--gray1)" mb="8px">
            {t("Export-Preview")}
          </Typography>
          <Box
            p="20px"
            borderRadius="8px"
            bgcolor="var(--gray6)"
            maxHeight="52vh"
            minHeight="220px"
            display="flex"
            flexDirection="column"
            sx={{ overflowY: "auto" }}
          >
            {snapshot && !isRendering ? (
              <Box
                component="img"
                src={snapshot.dataUrl}
                alt={t("Export-Preview")}
                sx={{
                  width: "100%",
                  height: "auto",
                  borderRadius: "4px",
                  border: "1px solid var(--gray5)",
                }}
              />
            ) : (
              <Box
                m="auto"
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap="12px"
                py="40px"
              >
                <CircularProgress sx={{ color: "var(--green)" }} />
                <Typography variant="body2" color="var(--gray1)">
                  {t("Export-Rendering")}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Footer */}
          <Box display="flex" justifyContent="flex-end" gap="8px" mt="20px">
            <Button
              variant="outlined"
              color="green"
              onClick={onClose}
              disabled={isExporting}
            >
              {t("Cancel")}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={
                isExporting ? (
                  <CircularProgress size={16} sx={{ color: "var(--white)" }} />
                ) : (
                  <FileDownloadIcon sx={{ fontSize: 18 }} />
                )
              }
              onClick={handleExport}
              disabled={isExporting || isRendering || !snapshot}
            >
              {t("Export-PDF")}
            </Button>
          </Box>
        </Box>
      }
    />
  );
};

export default ExportModal;
